import React, {
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { Col, Form, Input, Row } from 'antd';
import PropTypes from 'prop-types';

import { DASHBOARD_ITEM_API_NAMES } from '@src/constants/commons';
import PieChartService from '@src/services/PieChartService';

import BoxContainer from 'components/BoxContainer';
import Title from 'components/BoxContainer/components/Title';
import FormSelect from 'components/Form/components/FormSelect';

import { SlidePanelContext } from 'packages/dashboard/components/SlidePanel/SlidePanelContext';
import useGetEventTypes from 'packages/utils/hooks/useGetEventTypes';

import IntlMessages from 'util/IntlMessages';

import DropDownTriggers from '../../../../../../containers/DropDownTriggers';
import {
  DASHBOARDS_EVENT_TYPE,
  DASHBOARDS_FORMS,
  LAYOUT_BY_COMPONENT_TYPE,
} from '../../constants';
import { useEventsByScheduleContext } from '../../contexts/EventsByScheduleContext';

const PieChartForm = forwardRef(({ chartId, divId, boardId }, formRef) => {
  const intl = useIntl();
  const orgId = useSelector(state => state.organizations.organization.id);
  const { boardSelected } = useContext(SlidePanelContext);
  const {
    loading: schedulesLoading,
    eventsFromSchedules: formTypesFromSchedule,
    fetchEventsFromSchedules,
  } = useEventsByScheduleContext();
  const internalFormRef = useRef(null);
  const pieChartService = useMemo(() => new PieChartService(), []);
  const [formId, setFormId] = useState();
  const [eventIds, setEventIds] = useState([]);

  const [currentChart, setCurrentChart] = useState({});

  const [loading, setLoading] = useState(false);

  const handleClear = useCallback(() => {
    internalFormRef.current.setFieldsValue({
      title: '',
      formId: null,
      eventIds: [],
    });
    setFormId(null);
    setEventIds([]);
  }, []);

  useImperativeHandle(
    formRef,
    () => ({
      ...internalFormRef.current,
      handleClear,
      submit: () => {
        internalFormRef.current?.submit();
      },
    }),
    [handleClear],
  );

  const { data: formTypes, loading: formTypesLoading } = useGetEventTypes(
    true,
    boardSelected?.divId,
  );

  const mainLoading = schedulesLoading || formTypesLoading || loading;

  const divisionPerFormType = formTypes?.find(form => form.id === formId)?.divId || null;

  const onChangeEvents = useCallback(events => {
    const newEventsSelected = events.map(e => e.id);
    internalFormRef.current.setFieldValue('eventIds', newEventsSelected);
    setEventIds(newEventsSelected);
  }, []);

  const buildBody = useCallback(() => {
    const title = internalFormRef.current.getFieldValue('title');
    const currentFormId = internalFormRef.current.getFieldValue('formId');

    const body = {
      queryParams: {
        formId: currentFormId,
        eventIds,
      },
      layout: currentChart?.layout ?? LAYOUT_BY_COMPONENT_TYPE[DASHBOARDS_EVENT_TYPE.PIE_CHARTS],
      title,
      apiName: DASHBOARD_ITEM_API_NAMES.EVENTS_COUNTER,
    };
    if (internalFormRef.current) {
      internalFormRef.current.setFieldValue('chartFormData', body);
      internalFormRef.current.setFieldValue('id', chartId);
    }
  }, [currentChart, chartId, eventIds]);

  useEffect(() => {
    if (formId && orgId) {
      fetchEventsFromSchedules(orgId, divId, formId);
    }
  }, [formId, divId, fetchEventsFromSchedules, orgId]);

  useEffect(() => {
    if (chartId && boardId) {
      const fetchData = async () => {
        setLoading(true);
        const res = await pieChartService.getPieChart({ orgId, divId, boardId, chartId });
        if (res) setCurrentChart(res);
        setLoading(false);
      };
      fetchData();
    }
  }, [boardId, divId, orgId, chartId, pieChartService]);

  useEffect(() => {
    if (Object.values(currentChart).length > 0) {
      const { title, queryParams } = currentChart;
      const { eventIds: eventsIdsFetched = [], formId: formIdFetched = {} } = queryParams;

      internalFormRef.current.setFieldsValue({
        title,
        formId: formIdFetched,
        eventIds: eventsIdsFetched,
        id: chartId,
      });
      setEventIds(eventsIdsFetched);
      setFormId(formIdFetched);
    }
  }, [chartId, currentChart]);

  return (
    <Form
      onFieldsChange={buildBody}
      labelCol={{ span: 6 }}
      wrapperCol={{ span: 24 }}
      ref={internalFormRef}
      name={DASHBOARDS_FORMS.EVENT_COUNTER}
      initialValues={{
        id: chartId ?? null,
        title: internalFormRef.current?.getFieldValue('title') ?? '',
        formId: formId ?? null,
        eventIds: eventIds ?? [],
        chartFormData: {},
      }}
    >
      <BoxContainer content loading={mainLoading}>
        <Row gutter={16}>
          <Col span={24}>
            <Title.LabelForm
              required
              className="gx-guarnic-pb-1"
              value={intl.formatMessage({
                id: 'dashboards.chart.form.title',
              })}
            />
            <Form.Item
              rules={[
                {
                  required: true,
                  message: intl.formatMessage({
                    id: 'form.required',
                  }),
                },
              ]}
              name="title"
            >
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} md={8} sm={8} xxl={8}>
            <Title.LabelForm required value={<IntlMessages id="dashboards.chart.form.form" />} />
            <Form.Item
              rules={[
                {
                  required: true,
                  message: intl.formatMessage({
                    id: 'form.required',
                  }),
                },
              ]}
              name="formId"
            >
              <FormSelect
                onChange={val => {
                  setEventIds([]);
                  internalFormRef.current.setFieldValue('eventIds', []);

                  setFormId(val);
                }}
                options={formTypes}
                showSearch
                loading={formTypesLoading}
                value={formId}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={8} sm={8} xxl={8}>
            <Form.Item name="eventIds">
              <Title.LabelForm value={<IntlMessages id="general.eventTypes" />} />
              <DropDownTriggers
                mode="multiple"
                allowClear
                onChange={onChangeEvents}
                division={divisionPerFormType}
                eventType={formId}
                defaultValue={eventIds}
                value={eventIds}
                onClear={() => {
                  internalFormRef.current.setFieldValue('eventIds', []);
                  setEventIds(null);
                }}
                extraTriggers={formTypesFromSchedule}
                disabled={!formId}
              />
            </Form.Item>
          </Col>
        </Row>
      </BoxContainer>
      <Form.Item name="chartFormData" hidden />
      <Form.Item name="id" hidden />
    </Form>
  );
});

PieChartForm.propTypes = {
  chartId: PropTypes.object,
  divId: PropTypes.string,
  boardId: PropTypes.string,
};

export default PieChartForm;
